import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Crown, Zap, Loader2, CalendarClock, ShieldCheck, XCircle, CreditCard } from "lucide-react";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "");

export function SubscriptionManager() {
  const { checkAuth } = useAuth();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    api.get("/payments/subscription").then((r) => setSub(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const cancel = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/cancel");
      setSub(data); setConfirm(false);
      toast.success("Your subscription will cancel at the end of the billing period.");
      await checkAuth();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel subscription"); }
    finally { setBusy(false); }
  };

  const resume = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/resume");
      setSub(data);
      toast.success("Subscription resumed — it will keep renewing.");
      await checkAuth();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not resume subscription"); }
    finally { setBusy(false); }
  };

  const manageBilling = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/portal", { return_url: window.location.origin + "/account" });
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open billing portal"); setBusy(false); }
  };

  if (loading) return (
    <div className="eco-card p-6 flex items-center gap-3 text-slate-400" data-testid="subscription-loading"><Loader2 size={16} className="animate-spin" /> Loading subscription…</div>
  );

  if (!sub?.is_pro) return (
    <div className="eco-card glow-grid p-6 flex items-center justify-between gap-4" data-testid="subscription-upsell">
      <div className="flex items-center gap-3">
        <Crown size={20} className="text-amber-400" />
        <div><h3 className="font-display font-semibold text-white">Go Pro</h3><p className="text-sm text-slate-400">Unlock the full ECO toolkit.</p></div>
      </div>
      <Link to="/pricing" className="btn-accent shrink-0"><Zap size={15} /> Upgrade</Link>
    </div>
  );

  if (sub.is_admin_grant || !sub.has_subscription) return (
    <div className="eco-card p-6" data-testid="subscription-card">
      <div className="flex items-center gap-2 mb-1"><Crown size={18} className="text-amber-400" /><h3 className="font-display font-semibold text-white">ECO Pro</h3></div>
      <p className="text-sm text-slate-400">You have Pro access{sub.current_period_end ? ` until ${fmt(sub.current_period_end)}` : ""}. Enjoy the full toolkit.</p>
    </div>
  );

  const cancelling = sub.cancel_at_period_end;
  return (
    <div className="eco-card p-6" data-testid="subscription-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2"><Crown size={18} className="text-amber-400" /><h3 className="font-display font-semibold text-white">ECO Pro · {sub.interval === "year" ? "Yearly" : "Monthly"}</h3></div>
        <span className={`eco-tag ${cancelling ? "text-amber-300 border-amber-400/20 bg-amber-400/10" : "text-emerald-300 border-emerald-400/20 bg-emerald-400/10"}`} data-testid="subscription-status">{cancelling ? "Cancelling" : "Active"}</span>
      </div>
      <div className="flex items-center gap-2 text-sm text-slate-400 mt-3">
        <CalendarClock size={15} className="text-slate-500 shrink-0" />
        {cancelling ? `Your Pro access ends on ${fmt(sub.current_period_end)} — it won't renew.` : `Renews automatically on ${fmt(sub.current_period_end)}.`}
      </div>

      {cancelling ? (
        <button onClick={resume} disabled={busy} data-testid="resume-subscription-btn" className="btn-accent mt-5">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <><ShieldCheck size={15} /> Keep my subscription</>}
        </button>
      ) : null}

      <div className="flex flex-wrap gap-3 mt-5">
        <button onClick={manageBilling} disabled={busy} data-testid="manage-billing-btn" className="btn-accent">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <><CreditCard size={15} /> Manage billing</>}
        </button>
        {!cancelling && !confirm && (
          <button onClick={() => setConfirm(true)} data-testid="cancel-subscription-btn" className="btn-secondary"><XCircle size={15} /> Cancel</button>
        )}
      </div>
      <p className="text-xs text-slate-600 mt-2">Manage billing opens Stripe's secure portal to update your card, download invoices, change plan or cancel.</p>

      {confirm && !cancelling && (
        <div className="mt-4 bg-white/5 border border-white/10 rounded-xl p-4" data-testid="cancel-confirm">
          <p className="text-sm text-slate-300">Cancel your subscription? You'll keep Pro access until <span className="text-white font-medium">{fmt(sub.current_period_end)}</span>, then it won't renew.</p>
          <div className="flex flex-wrap gap-3 mt-4">
            <button onClick={cancel} disabled={busy} data-testid="confirm-cancel-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium bg-red-500/15 text-red-300 border border-red-400/30 hover:bg-red-500/25 transition-colors disabled:opacity-50">
              {busy ? <Loader2 size={15} className="animate-spin" /> : "Yes, cancel"}
            </button>
            <button onClick={() => setConfirm(false)} disabled={busy} className="btn-secondary">Keep subscription</button>
          </div>
        </div>
      )}
    </div>
  );
}
